// ============================================================
// FinancialStatements.jsx — Income Statement + Balance Sheet (Neil and Kurt)
//
// Backed by GET /api/reports/financial-statements, which enforces the
// two-person allowlist server-side — the nav gate (`ownerEmails` in
// constants/nav.js) is cosmetic.
//
// The page computes nothing: every line, subtotal, net income and the balance
// check come from the server's engine (api/src/financialStatements.js), and
// every caveat is rendered verbatim. Amounts are in their natural sign
// (revenue, liabilities and equity positive); a negative shows in parentheses.
// ============================================================
import React, { useEffect, useMemo, useState } from 'react';
import { B, font, space, weight, radius } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { saveXlsxResult } from '../utils/exportDownload';
import { Btn, Card, ErrorMsg, Input, PageHeader, Select, Spinner, Tabs, InfoNote } from '../components/ui';
import { InlineSearchPicker } from '../components/tasks/TaskComponents';
import { scopeItems, scopeMatcher, scopeFromValue } from '../utils/financialStatementScope';

const REPORT_TIMEOUT_MS = 120000;
const STATEMENT_TABS = ['Income Statement', 'Balance Sheet'];
const COMPARE_OPTIONS = [
  { value: 'none', label: 'No comparison' },
  { value: 'priorYear', label: 'Same period last year' },
  { value: 'priorPeriod', label: 'Prior period of the same length' },
];

const amt = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) < 0.005) return '—';
  const s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};

const cell = { padding: '6px 10px', fontSize: font.sm, borderBottom: `1px solid ${B.divider}` };
const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

function HeaderRow({ columns }) {
  return (
    <tr style={{ background: B.surfaceMuted }}>
      <th style={{ ...cell, textAlign: 'left', width: 90 }}>Account</th>
      <th style={{ ...cell, textAlign: 'left' }}>Name</th>
      {columns.map(c => (
        <th key={c.label} style={{ ...num, fontWeight: weight.semibold }}>
          <div>{c.label}</div>
          <div style={{ fontSize: font.xs, color: B.textMd, fontWeight: weight.medium }}>
            {c.from === c.to ? c.to : `${c.from} – ${c.to}`}
          </div>
        </th>
      ))}
    </tr>
  );
}

function LineRows({ lines, indent }) {
  return lines.map((l, i) => (
    <tr key={`${l.account || l.name}-${i}`}>
      <td style={{ ...cell, color: B.textMd }}>{l.account || ''}</td>
      <td style={{ ...cell, paddingLeft: indent ? 26 : 10, fontStyle: l.computed ? 'italic' : 'normal' }}>{l.name || <span style={{ color: B.textLt }}>(no name in the chart of accounts)</span>}</td>
      {l.values.map((v, j) => <td key={j} style={num}>{amt(v)}</td>)}
    </tr>
  ));
}

function SectionRows({ section, totalLabel }) {
  const span = 2 + section.total.length;
  const grouped = section.groups && section.groups.length > 0;
  return (
    <>
      <tr><td colSpan={span} style={{ ...cell, fontWeight: weight.semibold, color: B.navy, paddingTop: 14 }}>{section.label}</td></tr>
      {section.lines.length === 0 && (
        <tr><td colSpan={span} style={{ ...cell, color: B.textLt, fontStyle: 'italic' }}>No balances</td></tr>
      )}
      {grouped ? section.groups.map(g => (
        <React.Fragment key={g.key}>
          <tr><td style={cell} /><td colSpan={span - 1} style={{ ...cell, fontWeight: weight.medium, color: g.key === 'unmapped' ? B.red : B.text }}>{g.label}</td></tr>
          <LineRows lines={g.lines} indent />
          <tr>
            <td style={cell} />
            <td style={{ ...cell, paddingLeft: 26, color: B.textMd }}>Total {g.label}</td>
            {g.total.map((v, j) => <td key={j} style={{ ...num, color: B.textMd, borderTop: `1px solid ${B.divider}` }}>{amt(v)}</td>)}
          </tr>
        </React.Fragment>
      )) : <LineRows lines={section.lines} />}
      <TotalRow label={totalLabel || `Total ${section.label.toLowerCase()}`} values={section.total} />
    </>
  );
}

function TotalRow({ label, values, strong, tone }) {
  return (
    <tr style={{ background: strong ? B.surfaceMuted : 'transparent' }}>
      <td style={cell} />
      <td style={{ ...cell, fontWeight: strong ? weight.bold : weight.semibold, color: tone || B.text }}>{label}</td>
      {values.map((v, j) => (
        <td key={j} style={{ ...num, fontWeight: strong ? weight.bold : weight.semibold, color: tone || B.text, borderTop: `1px solid ${B.border}` }}>{amt(v)}</td>
      ))}
    </tr>
  );
}

function IncomeStatement({ report }) {
  const is = report.incomeStatement;
  const [rev, cogs, exp, oth] = is.sections;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead><HeaderRow columns={report.columns} /></thead>
      <tbody>
        <SectionRows section={rev} />
        <SectionRows section={cogs} />
        <TotalRow label="Gross profit" values={is.grossProfit} strong />
        <SectionRows section={exp} />
        <TotalRow label="Operating income" values={is.operatingIncome} strong />
        <SectionRows section={oth} totalLabel="Total other income / (expense)" />
        <TotalRow label="Net income" values={is.netIncome} strong />
        {is.unclassified.lines.length > 0 && <SectionRows section={is.unclassified} />}
      </tbody>
    </table>
  );
}

function BalanceSheet({ report }) {
  const bs = report.balanceSheet;
  const off = bs.difference.some(d => Math.abs(d) >= 0.005);
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead><HeaderRow columns={report.columns.map(c => ({ ...c, from: c.to }))} /></thead>
      <tbody>
        <SectionRows section={bs.assets} />
        <SectionRows section={bs.liabilities} />
        <SectionRows section={bs.equity} />
        <TotalRow label="Total liabilities + equity" values={bs.liabilitiesAndEquity} strong />
        <TotalRow label={off ? 'Out of balance (assets − liabilities − equity)' : 'Balanced'}
          values={bs.difference} tone={off ? B.red : B.green} />
        {bs.unclassified.lines.length > 0 && <SectionRows section={bs.unclassified} />}
      </tbody>
    </table>
  );
}

export function FinancialStatements() {
  const opts = useApi(() => api.get('reports/financial-statements-options'), []);
  const [pick, setPick] = useState('consolidated');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [compare, setCompare] = useState('none');
  const [statement, setStatement] = useState('Income Statement');
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState(null);

  const o = opts.data;
  // Default the period to this year to date, once the server has said what
  // today is (Central). The person can change either date afterwards.
  useEffect(() => {
    if (o?.today && !from && !to) {
      setFrom(`${o.today.slice(0, 4)}-01-01`);
      setTo(o.today);
    }
  }, [o?.today]); // eslint-disable-line react-hooks/exhaustive-deps

  const { scope, entity, dealId } = scopeFromValue(pick);
  const items = useMemo(() => (o?.available ? scopeItems(o) : []), [o]);
  const match = useMemo(() => scopeMatcher(items), [items]);
  const ready = !!o?.available && !!from && !!to && (scope === 'consolidated' || (scope === 'entity' && entity) || (scope === 'deal' && dealId));

  const params = useMemo(() => {
    const p = { scope, compare };
    if (scope === 'entity') p.entity = entity;
    if (scope === 'deal') p.dealId = dealId;
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [scope, entity, dealId, from, to, compare]);
  const qs = new URLSearchParams(params).toString();

  const report = useApi(
    () => (ready ? api.get(`reports/financial-statements?${qs}`, { timeout: REPORT_TIMEOUT_MS }) : Promise.resolve(null)),
    [qs, ready]);

  const onExport = async () => {
    setExporting(true); setExportErr(null);
    try {
      const res = await api.post('reports/financial-statements/export', params, { timeout: REPORT_TIMEOUT_MS });
      saveXlsxResult(res);
    } catch (e) {
      setExportErr(e.message || 'The export failed.');
    } finally {
      setExporting(false);
    }
  };

  const r = report.data;

  return (
    <div>
      <PageHeader title="Financial Statements" icon="ledger"
        subtitle="Income statement and balance sheet from the Sage General Ledger"
        actions={r && <Btn variant="secondary" onClick={onExport} loading={exporting}>Export to Excel</Btn>} />

      {opts.loading && <Spinner />}
      {opts.error && <ErrorMsg msg={opts.error} onRetry={opts.reload} />}
      {o && o.available === false && <InfoNote>{o.reason}</InfoNote>}

      {o?.available && (
        <Card style={{ padding: space.lg, marginBottom: space.lg }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: space.md }}>
            <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
              <InlineSearchPicker label="Consolidated, entity or deal" items={items} value={pick}
                onChange={(v) => setPick(v || '')} match={match}
                placeholder="Search entities and deals…" clearLabel="— Pick one —" />
            </div>
            <Input label="Period from" type="date" value={from} onChange={setFrom} />
            <Input label="Period to / as of" type="date" value={to} onChange={setTo} />
            <Select label="Compare to" value={compare} onChange={setCompare} options={COMPARE_OPTIONS} />
          </div>
          <div style={{ fontSize: font.xs, color: B.textMd }}>
            Defaults to this year to date. The balance sheet is as of the “to” date. A deal is listed under every entity it rolls up to; deals that roll up to none are at the end.
          </div>
        </Card>
      )}

      {!ready && o?.available && <InfoNote>{!from || !to ? 'Pick a period.' : 'Pick Consolidated, an entity or a deal to see its statements.'}</InfoNote>}
      {ready && report.loading && <Spinner label="Reading the General Ledger…" />}
      {ready && report.error && <ErrorMsg msg={report.error} onRetry={report.reload} />}
      {exportErr && <ErrorMsg msg={exportErr} />}

      {ready && r && !report.loading && (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: space.md, flexWrap: 'wrap', marginBottom: space.sm }}>
            <div style={{ fontSize: font.lg, fontWeight: weight.semibold, color: B.navy }}>{r.scopeLabel}</div>
            <div style={{ fontSize: font.xs, color: B.textMd }}>
              {r.coverage.postings.toLocaleString('en-US')} GL postings
              {r.coverage.firstDate ? `, ${r.coverage.firstDate} to ${r.coverage.lastDate}` : ''}
            </div>
          </div>
          {r.caveats?.length > 0 && (
            <div style={{ background: B.surfaceMuted, border: `1px solid ${B.divider}`, borderRadius: radius.lg, padding: space.md, marginBottom: space.lg }}>
              {r.caveats.map((c, i) => <div key={i} style={{ fontSize: font.xs, color: B.textMd, marginBottom: 4 }}>• {c}</div>)}
            </div>
          )}
          <Tabs tabs={STATEMENT_TABS} active={statement} onChange={setStatement} />
          <Card style={{ padding: 0, overflowX: 'auto' }}>
            {statement === 'Income Statement' ? <IncomeStatement report={r} /> : <BalanceSheet report={r} />}
          </Card>
        </>
      )}
    </div>
  );
}

export default FinancialStatements;
